import { createFlUser } from './common-helper';
import { getAccessToken } from './idm-helpers';
import * as store from './store';

/**
 * Generic fetch wrapper that can inject Sidekick user-context headers (currently FlUser) into requests.
 * @param {string} url - The URL to fetch.
 * @param {object} [options] - Fetch options + user-context toggles.
 * @param {string} [options.method='GET'] - HTTP method.
 * @param {object|Headers} [options.headers] - Request headers (may be extended).
 * @param {*} [options.body] - Request body.
 * @param {boolean} [options.includeFlUser=true] - Inject FlUser header.
 * @param {boolean} [options.includeToken=true] - Inject Authorization Bearer token.
 * @param {boolean} [options.includeCredentials=false] - Include credentials (cookies) in cross-origin requests.
 * @returns {Promise<Response>} - Fetch response (throws on !ok).
 */
export const httpClient = async (url, options = {}) => {
    const {
        method = 'GET',
        headers = {},
        body,
        includeFlUser = true,
        includeToken = true
    } = options;
    // Collect remaining (standard fetch) options excluding our custom flags
    const rest = {};
    for (const k in options) {
        if (!['method','headers','body','includeFlUser','includeToken'].includes(k) && Object.prototype.hasOwnProperty.call(options, k)) {
            rest[k] = options[k];
        }
    }

    // Normalize headers to a mutable Headers instance (clone to avoid side effects on caller input)
    const mergedHeaders = new Headers(headers);

    const { flUserSource, flUser, getToken, includeCredentials } = store.get('api-user-context') || {};

    // FlUser handling
    if (includeFlUser) {
        let flUserData;
        // Try custom flUser function first
        if (typeof flUser === 'function') {
            try { flUserData = await flUser(); } catch (e) { /* silent fallback */ }
        }
        // Next, try to create from source if not provided
        if (!flUserData) {
            flUserData = createFlUser(flUserSource);
        }
        mergedHeaders.set('FlUser', flUserData ? (typeof flUserData === 'string' ? flUserData : JSON.stringify(flUserData)) : '');
    } else {
        mergedHeaders.delete('FlUser');
    }

    // Authorization token handling
    if (includeToken) {
        // Try to get token from stored supplier functions
        const tokenSupplier = typeof getToken === 'function' ? getToken : (typeof getAccessToken === 'function' ? getAccessToken : null);
        if (tokenSupplier) {
            try {
                const token = tokenSupplier();
                if (token) { mergedHeaders.set('Authorization', `Bearer ${token}`); }
            } catch (e) { /* ignore token retrieval errors */ }
        }
    } else {
        mergedHeaders.delete('Authorization');
    }

    // Build final fetch options with credentials if requested
    const fetchOptions = Object.assign({}, rest, { method, body, headers: mergedHeaders });
    if (includeCredentials) {
        fetchOptions.credentials = 'include';
    }

    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
    }
    return response;
};
